/**
 * CCUsersSelector - A reusable component for selecting organization users to CC on requests
 *
 * This component provides a dropdown to select users from the current organization
 * and displays their emails in the placeholder. It fetches all org users and excludes the current user.
 *
 * Usage:
 * - Used in SupportRequestModal for CC'ing users on support requests
 * - Used in UpdateNameModal for CC'ing users on name change requests
 *
 * Note: The actual CC functionality depends on the underlying request function supporting it.
 * Currently working for name change requests, support requests require API update.
 */

import { useState, useEffect } from 'react'
import { Fa, FormItem, Select } from '@eltoro-ui/components'
import { Orgmanagerv1User, V1ListOrgUsersResponse } from 'next-gen-sdk'
import { useAuth } from 'react-oidc-context'
import { useAppContext } from '../../Contexts'

export interface CCUser {
  id: string
  name: string
  email: string
}

export interface CCUsersSelectorProps {
  selectedUsers: CCUser[]
  onSelectedUsersChange: (users: CCUser[]) => void
  label?: string
  description?: string
  className?: string
}

export const CCUsersSelector = ({
  selectedUsers,
  onSelectedUsersChange,
  className,
}: CCUsersSelectorProps) => {
  const [orgUsers, setOrgUsers] = useState<Orgmanagerv1User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const { orgServiceApi, currentOrg } = useAppContext()
  const auth = useAuth()

  // Fetch org users for CC selection
  useEffect(() => {
    const fetchOrgUsers = async () => {
      if (!orgServiceApi || !currentOrg?.id) return

      setLoadingUsers(true)
      try {
        let allUsers: Orgmanagerv1User[] = []
        let nextPageToken: string | undefined = undefined

        do {
          const response: V1ListOrgUsersResponse = await orgServiceApi.advertisingPlatformServiceListOrgUsers(
            currentOrg.id,
            100, // pageSize
            nextPageToken, // pageToken
            'first_name', // orderBy
            undefined, // filter
          )

          if (response?.users) {
            allUsers = [...allUsers, ...response.users]
          }

          nextPageToken = response?.nextPageToken
        } while (nextPageToken)

        setOrgUsers(allUsers)
      } catch (error) {
        console.error('Failed to fetch org users:', error)
      } finally {
        setLoadingUsers(false)
      }
    }

    fetchOrgUsers()
  }, [orgServiceApi, currentOrg?.id])

  const handleUserSelect = (
    option: { label: any; value?: string } | undefined,
  ) => {
    if (option?.value) {
      const user = orgUsers.find((u) => u.id === option.value)
      if (user && !selectedUsers.find((u) => u.id === user.id)) {
        const newUser: CCUser = {
          id: user.id || '',
          name: `${user.firstName} ${user.lastName}`,
          email: user.email || '',
        }
        onSelectedUsersChange([...selectedUsers, newUser])
      }
    }
  }

  const handleRemoveUser = (userId: string) => {
    const updatedUsers = selectedUsers.filter((user) => user.id !== userId)
    onSelectedUsersChange(updatedUsers)
  }

  // Generate placeholder text - simplified since selected users are shown separately
  const getPlaceholder = () => {
    return 'Select users to add as recipients'
  }

  return (
    <div className={className}>
      <FormItem
        htmlFor="cc-users"
        label={<p className="mb-2 text-lg font-bold">Additional Recipients</p>}
        wrapperClassname="flex-col"
      >
        <Select
          placeholder={getPlaceholder()}
          options={orgUsers
            .filter((user) => user.id !== auth.user?.profile.sub) // Exclude current user
            .map((user) => {
              const isSelected = selectedUsers.find((u) => u.id === user.id)
              return {
                label: (
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="font-bold">
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="text-sm text-tint-gray-600">
                        {user.email}
                      </span>
                    </div>
                    {isSelected && <Fa icon="check" size={1} />}
                  </div>
                ),
                value: user.id || '',
              }
            })}
          selectedOption={undefined}
          onChange={handleUserSelect}
          disabled={loadingUsers}
          enableSearch
          maxHeight={250}
          searchInputPlaceholder="Search users"
          selectClassName="!py-4"
        />
      </FormItem>

      {/* Display selected users with remove buttons */}
      {selectedUsers.length > 0 && (
        <div className="mt-3">
          <p className="mb-2 text-sm font-medium text-tint-gray-700">
            Selected Recipients:
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-2 rounded-md bg-tint-khaki-50 px-3 py-1 text-sm"
              >
                <span className="font-medium">{user.name}</span>
                <span className="opacity-80">({user.email})</span>
                <button
                  type="button"
                  onClick={() => handleRemoveUser(user.id)}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                  aria-label={`Remove ${user.name}`}
                >
                  <Fa icon="times" size={0.75} />
                  <span className="sr-only">{`Remove ${user.name}`}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
